import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";

import { useAppContext } from "@/contexts/AppContext";

/**
 * Query parameter that opens a page directly in presentation, e.g. the URL a
 * war-room kiosk loads (`/workspace/dashboards/<slug>?present=1`). It is kept
 * while presenting so a reload, or a return from sign-in, presents again.
 */
export const PRESENT_PARAM = "present";

/** True while a dialog is actually open: closed drawers stay mounted but are aria-hidden / inert. */
export function hasOpenDialog(root: ParentNode = document): boolean {
  return Array.from(root.querySelectorAll('[role="dialog"], [role="alertdialog"]')).some(
    (dialog) => !dialog.closest('[aria-hidden="true"], [inert]'),
  );
}

function isTyping(target: EventTarget | null): boolean {
  return (
    target instanceof Element &&
    target.closest("input, textarea, select, [contenteditable], [role='textbox']") !== null
  );
}

/**
 * Full-screen presentation of a page (war-room TVs). Entering also asks the
 * browser for fullscreen, falling back to the chrome-free app view when it is
 * refused; Escape leaves, and Shift+F toggles when the page offers it.
 *
 * Presentation is also reflected in the URL (see {@link PRESENT_PARAM}). Opening
 * such a URL presents without asking for browser fullscreen, which needs a user
 * gesture: a kiosk browser is already full screen.
 */
export function usePresentationMode(available: boolean) {
  const { dashboardFullscreen, dashboardFullscreenWidgetId, setDashboardFullscreen } =
    useAppContext();
  const active = available && dashboardFullscreen && dashboardFullscreenWidgetId === null;
  const requestedBrowserFullscreen = useRef(false);
  const leavingPresentation = useRef(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedByUrl = searchParams.get(PRESENT_PARAM) === "1";

  function setUrlPresentation(present: boolean) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (present) next.set(PRESENT_PARAM, "1");
        else next.delete(PRESENT_PARAM);
        return next;
      },
      { replace: true },
    );
  }

  // Kept in a ref so the listeners below always call the latest closures.
  const actions = useRef({ enter: () => {}, leave: () => {}, exit: () => {} });
  actions.current = {
    enter() {
      leavingPresentation.current = false;
      // Requested from the click/key event: browser fullscreen needs a user gesture.
      if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
        requestedBrowserFullscreen.current = true;
        void document.documentElement.requestFullscreen().then(
          () => {
            // The viewer may have left while the browser prompt was still pending.
            if (
              !requestedBrowserFullscreen.current &&
              document.fullscreenElement === document.documentElement
            )
              void document.exitFullscreen().catch(() => undefined);
          },
          () => {
            requestedBrowserFullscreen.current = false;
          },
        );
      }
      setDashboardFullscreen(true);
      setUrlPresentation(true);
    },
    leave() {
      leavingPresentation.current = true;
      setDashboardFullscreen(false);
      setUrlPresentation(false);
    },
    exit() {
      if (
        requestedBrowserFullscreen.current &&
        document.fullscreenElement === document.documentElement
      )
        void document.exitFullscreen().catch(() => undefined);
      requestedBrowserFullscreen.current = false;
      actions.current.leave();
    },
  };

  useEffect(() => {
    function syncBrowserFullscreen() {
      if (requestedBrowserFullscreen.current && !document.fullscreenElement) {
        requestedBrowserFullscreen.current = false;
        actions.current.leave();
      }
    }
    document.addEventListener("fullscreenchange", syncBrowserFullscreen);
    return () => {
      document.removeEventListener("fullscreenchange", syncBrowserFullscreen);
      if (
        requestedBrowserFullscreen.current &&
        document.fullscreenElement === document.documentElement
      )
        void document.exitFullscreen().catch(() => undefined);
    };
  }, [setDashboardFullscreen]);

  // A URL asking for presentation enters it once the page offers it, and again
  // after a focused widget is closed.
  useEffect(() => {
    if (!requestedByUrl) leavingPresentation.current = false;
    if (available && requestedByUrl && !dashboardFullscreen && !leavingPresentation.current)
      setDashboardFullscreen(true);
  }, [available, requestedByUrl, dashboardFullscreen, setDashboardFullscreen]);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (
        event.defaultPrevented ||
        (event.target instanceof Element &&
          event.target.closest('[data-escape-key-handled="true"]'))
      )
        return;
      if (event.key === "Escape") {
        if (dashboardFullscreen && !hasOpenDialog()) actions.current.exit();
        return;
      }
      const shortcut =
        event.key.toLowerCase() === "f" &&
        event.shiftKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.metaKey &&
        !event.repeat;
      if (
        !shortcut ||
        !available ||
        dashboardFullscreenWidgetId !== null ||
        hasOpenDialog() ||
        isTyping(event.target)
      )
        return;
      event.preventDefault();
      if (dashboardFullscreen) actions.current.exit();
      else actions.current.enter();
    }
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [available, dashboardFullscreen, dashboardFullscreenWidgetId]);

  return {
    active,
    enter: () => actions.current.enter(),
    exit: () => actions.current.exit(),
  };
}
